import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FiMenu, FiX, FiChevronDown, FiChevronRight } from "react-icons/fi";
import logo from "../../assets/logo.png";

const CATEGORIES = [
  { id: "windows-doors", name: "Windows & Door" },
  { id: "louvers-system", name: "Louvers & System" },
  { id: "railings", name: "Railing Systems" },
];

const SUBCATEGORIES = {
  "windows-doors": [
    "HL Imperial Slim System",
    "HL Vista Slim System",
    "HL Elite Gulf Slim System",
    "HL Ultra Slim System",
    "HL Retro Gulf Slim System",
    "HL Eco Gulf Slim System",
    "HL Sliding Folding System",
    "HL 50 Casement Door",
    "HL 40 Casement Window",
    "HL 2560 Partition System",
  ],
  "louvers-system": [
    "Linear 64C / 84C / 184C",
    "Linear 64R / 84R / 184R",
    "Linear 100C / 150C / 200C",
    "U Shape Baffles",
    "V Shape Baffles",
    "Round Baffles",
    "Open Cell Ceiling System",
    "Sun Louvers",
  ],
  railings: [
    "ERT01-Handrail",
    "ERT02-Handrail",
    "ERT03-Handrail",
    "ERT04-Handrail",
    "ERT05-Handrail",
    "ERT06-Handrail",
    "ERB01-Bottomrail",
    "ERB02-Bottomrail",
    "ERB03-Bottomrail",
    "ERB04-Bottomrail",
    "ERB05-Bottomrail",
    "ERB06-Bottomrail",
  ],
};

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].id);
  const [showProducts, setShowProducts] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const goProducts = (catId, sub = null) => {
    const params = new URLSearchParams({ cat: catId });
    if (sub) params.set("sub", sub);
    navigate(`/products?${params.toString()}`);
    setShowProducts(false);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };

    if (productsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [productsOpen]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur border-b border-white/10 shadow-header">
      <div className="container flex items-center justify-between h-20 text-sm tracking-wide">
        <NavLink to="/" className="flex items-center">
          <img src={logo} alt="Logo" className="h-10 w-auto" />
        </NavLink>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${
                isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            HOME
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${
                isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            ABOUT
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setProductsOpen((v) => !v)}
              className="flex items-center gap-1 text-white/80 hover:text-white font-medium"
            >
              PRODUCTS
              <FiChevronDown
                className={`text-xs transition-transform ${
                  productsOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {productsOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-4 w-[750px] bg-neutral-900/95 border border-white/10 rounded-xl shadow-xl p-4 backdrop-blur">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-5 border-r border-white/10 pr-3">
                    <h4 className="text-xs uppercase tracking-[0.25em] text-white/60 mb-2">
                      Categories
                    </h4>
                    <ul className="space-y-1">
                      {CATEGORIES.map((cat) => (
                        <li key={cat.id}>
                          <button
                            onMouseEnter={() => setActiveCat(cat.id)}
                            onClick={() => goProducts(cat.id)}
                            className={`w-full text-left px-2 py-2 rounded-md ${
                              activeCat === cat.id
                                ? "bg-white/10 text-white"
                                : "text-white/80 hover:bg-white/5"
                            }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <FiChevronRight
                                className={`transition ${
                                  activeCat === cat.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              {cat.name}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="col-span-7 pl-3">
                    <h4 className="text-xs uppercase tracking-[0.25em] text-white/60 mb-2">
                      Subcategories
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {(SUBCATEGORIES[activeCat] || []).map((sub) => (
                        <button
                          key={sub}
                          onClick={() => goProducts(activeCat, sub)}
                          className="text-left px-3 py-2 rounded-md border border-white/10 bg-white/[0.05] text-white/90 hover:bg-white/10 transition"
                        >
                          {sub}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${
                isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            PROJECTS
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${
                isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            CONTACT
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <FiX size={26} /> : <FiMenu size={26} />}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden bg-neutral-900 border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <ul className="p-5 space-y-2">
            <li>
              <NavLink
                to="/"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                HOME
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                ABOUT
              </NavLink>
            </li>

            <li>
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer py-2 text-white/90">
                  PRODUCTS
                  <FiChevronDown className="group-open:rotate-180 transition" />
                </summary>
                <div className="pl-4">
                  {CATEGORIES.map((cat) => (
                    <div key={cat.id} className="mb-2">
                      <button
                        onClick={() => goProducts(cat.id)}
                        className="text-white font-medium text-sm"
                      >
                        {cat.name}
                      </button>
                      <ul className="ml-2 mt-1 space-y-1">
                        {(SUBCATEGORIES[cat.id] || []).map((sub) => (
                          <li key={sub}>
                            <button
                              onClick={() => goProducts(cat.id, sub)}
                              className="block text-white/70 hover:text-white text-xs"
                            >
                              — {sub}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            </li>

            <li>
              <NavLink
                to="/projects"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                PROJECTS
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                CONTACT
              </NavLink>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
